import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getImageUrl, getProfileImageUrl } from '../utils/imageUtils';
import { formatFarmerLocation } from '../utils/addressUtils';
import { toLocalizedNumber, toLocalizedPrice } from '../utils/localeUtils';
import { getStockStatusFromProduct } from '../utils/productStockUtils';
import { useTranslation } from 'react-i18next';
import { FaLeaf, FaStar, FaCheckCircle, FaShoppingCart, FaCheck, FaMapMarkerAlt } from 'react-icons/fa';
import './ProductCard.css';

const ProductCard = ({ product, user, onAddToCart }) => {
  const { t } = useTranslation();
  const [added, setAdded] = useState(false);

  const stockStatus = getStockStatusFromProduct(product);
  const isOutOfStock = stockStatus === 'out-of-stock';
  const isLowStock = stockStatus === 'low-stock';

  const handleAdd = () => {
    if (isOutOfStock || !onAddToCart) return;
    onAddToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const stockLabel = isOutOfStock
    ? t('common.outOfStock')
    : isLowStock
      ? t('common.lowStock')
      : t('common.inStock');

  const stockClass = isOutOfStock ? 'pc-stock--out' : isLowStock ? 'pc-stock--low' : 'pc-stock--in';

  const imageSrc = product.image || product.images?.[0];

  return (
    <div className={`pc ${isOutOfStock ? 'pc--disabled' : ''}`}>
      {/* Image Area */}
      <Link to={`/products/${product._id}`} className="pc-img-link">
        <div className="pc-img-wrap">
          {imageSrc ? (
            <img
              src={getImageUrl(imageSrc)}
              alt={product.name}
              className="pc-img"
              referrerPolicy="no-referrer"
              onError={(e) => { e.target.onerror = null; e.target.src = '/images/deafultpic.png'; }}
            />
          ) : (
            <div className="pc-img-placeholder">
              <FaLeaf />
            </div>
          )}

          {/* Overlay gradient at bottom of image */}
          <div className="pc-img-overlay" />

          {/* Badges */}
          <div className="pc-badges">
            {product.organic && (
              <span className="pc-badge pc-badge--organic"><FaLeaf /> {t('common.organic')}</span>
            )}
            <span className={`pc-badge ${stockClass}`}>{stockLabel}</span>
          </div>

          {/* Price tag floating on image */}
          <div className="pc-price-float">
            <span className="pc-price-rs">Rs.</span>
            <span className="pc-price-val">{toLocalizedPrice(product.price)}</span>
            <span className="pc-price-unit">/{product.unit}</span>
          </div>
        </div>
      </Link>

      {/* Body */}
      <div className="pc-body">
        <Link to={`/products/${product._id}`} className="pc-name-link">
          <h3 className="pc-name">{product.name}</h3>
        </Link>

        {/* Farmer row */}
        <div className="pc-farmer">
          <img
            src={getProfileImageUrl(product.farmer?.profileImage)}
            alt={product.farmer?.name}
            className="pc-farmer-avatar"
            referrerPolicy="no-referrer"
          />
          <div className="pc-farmer-info">
            <Link to={`/users/${product.farmer?.username || product.farmer?._id}`} className="pc-farmer-name">
              {product.farmer?.name}
              {product.farmer?.verified && <FaCheckCircle className="pc-verified" />}
            </Link>
            <span className="pc-farmer-loc"><FaMapMarkerAlt /> {formatFarmerLocation(product.farmer)}</span>
          </div>
          <div className="pc-rating">
            <FaStar className="pc-rating-star" />
            <span>{toLocalizedNumber(product.rating || 0)}</span>
          </div>
        </div>

        {/* Action */}
        <div className="pc-action">
          {isOutOfStock ? (
            <button className="pc-btn pc-btn--disabled" disabled>{t('common.outOfStock')}</button>
          ) : user && user.role === 'farmer' && product.farmer && (user.id === product.farmer._id || user._id === product.farmer._id) ? (
            <Link to={`/farmer/products/edit/${product._id}`} className="pc-btn pc-btn--manage">{t('productCard.manage')}</Link>
          ) : !user || user.role !== 'consumer' ? (
            <Link to="/login" className="pc-btn pc-btn--primary">{t('productCard.loginToBuy')}</Link>
          ) : (
            <button className={`pc-btn pc-btn--cart ${added ? 'pc-btn--added' : ''}`} onClick={handleAdd}>
              {added ? <><FaCheck /> {t('productCard.addedToCart')}</> : <><FaShoppingCart /> {t('productCard.addToCart')}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
